/**
 * Kunden (CRM-leicht, Stitch: maestro_kunden + maestro_kunden_profil):
 * Filter-Chips + Suchleiste + 7-Spalten-Tabelle; Zeile navigiert zur Vollseite
 * pages/customer-detail.tsx (H4 — vorher Drawer). "Kunde anlegen"-Drawer bleibt hier.
 */
import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import type { Customer, EventRow } from "../ui/types";
import { formatDate, money } from "../ui/format";
import { Drawer, EmptyState, ErrorNote, Icon, LoadingRows, PageHeader } from "../ui/components";

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2);
  const out = parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
  return out || "?";
}

/** H2: Kunden-Typ aus der Firma abgeleitet (kein eigenes Feld im Datenmodell). */
type CustomerType = "privat" | "firma" | "verein";
function customerType(c: Customer): CustomerType {
  if (!c.company) return "privat";
  return /\be\.\s?v\.?\b|verein/i.test(c.company) ? "verein" : "firma";
}

const OPEN_EVENT_STATUS = new Set(["offer_sent", "offer_chosen", "payment_failed"]);
const LOST_EVENT_STATUS = new Set(["cancelled", "offer_declined", "no_response"]);

export default function CustomersPage() {
  const { data, loading, error, reload } = useApi<Customer[]>("/api/customers");
  const eventsQ = useApi<EventRow[]>("/api/events");
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | CustomerType>("all");

  const customers = data ?? [];
  const events = eventsQ.data ?? [];
  const typeCounts = useMemo(() => {
    const counts = { privat: 0, firma: 0, verein: 0 };
    for (const c of customers) counts[customerType(c)]++;
    return counts;
  }, [customers]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return customers.filter((c) => {
      if (typeFilter !== "all" && customerType(c) !== typeFilter) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.company ?? "").toLowerCase().includes(q)
      );
    });
  }, [customers, search, typeFilter]);

  const lifetimeSumAllCents = useMemo(
    () => customers.reduce((s, c) => s + events.filter((e) => e.customerId === c.id && ["paid", "completed"].includes(e.status)).reduce((s2, e) => s2 + (e.amountTotalCents ?? 0), 0), 0),
    [customers, events],
  );
  const topCustomer = useMemo(() => {
    let best: { name: string; revenue: number } | null = null;
    for (const c of customers) {
      const revenue = events.filter((e) => e.customerId === c.id && ["paid", "completed"].includes(e.status)).reduce((s, e) => s + (e.amountTotalCents ?? 0), 0);
      if (!best || revenue > best.revenue) best = { name: c.company || c.name, revenue };
    }
    return best;
  }, [customers, events]);

  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate("/api/customers", "POST", {
        name: form.get("name"),
        email: form.get("email"),
        company: (form.get("company") as string) || undefined,
        phone: (form.get("phone") as string) || undefined,
      });
      setCreateOpen(false);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Kunden"
        subtitle={customers.length > 0 ? `${customers.length} Kunden · ${money(lifetimeSumAllCents)} Lifetime-Umsatz` : "Kontakte, Historie und Umsätze Ihrer Kunden."}
        actions={
          <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
            <Icon name="person_add" className="text-[18px]" /> Kunde anlegen
          </button>
        }
      />

      {/* H2: Filter-Chips Alle/Privat/Firmen/Vereine */}
      <div className="mb-4 flex flex-wrap gap-2">
        {(
          [
            { key: "all", label: "Alle", count: customers.length },
            { key: "privat", label: "Privat", count: typeCounts.privat },
            { key: "firma", label: "Firmen", count: typeCounts.firma },
            { key: "verein", label: "Vereine", count: typeCounts.verein },
          ] as const
        ).map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setTypeFilter(f.key)}
            className={`rounded-full px-3.5 py-1.5 font-label-md text-[11.5px] font-semibold ${
              typeFilter === f.key
                ? "bg-on-background text-inverse-on-surface dark:bg-inverse-on-surface dark:text-inverse-surface"
                : "border border-outline-variant/60 bg-surface-container-lowest text-on-surface-variant dark:border-outline/40 dark:bg-inverse-surface dark:text-secondary-fixed-dim"
            }`}
          >
            {f.label} {f.count}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {/* Suchleiste */}
        <div className="border-b border-outline-variant/30 p-4 dark:border-outline/30">
          <div className="group relative max-w-sm">
            <Icon
              name="search"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant transition-colors group-focus-within:text-primary dark:text-secondary-fixed-dim"
            />
            <input
              className="input !pl-10"
              placeholder="Suchen (Name, Firma, E-Mail) …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {error && <ErrorNote message={error} onRetry={reload} />}
        {loading ? (
          <LoadingRows />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="group"
            title="Keine Kunden gefunden"
            hint={search ? "Andere Suche versuchen." : "Legen Sie Ihren ersten Kunden an."}
            cta={search ? undefined : { label: "Kunde anlegen", icon: "person_add", onClick: () => setCreateOpen(true) }}
          />
        ) : (
          <div className="overflow-x-auto">
            {/* H1: 7 Spalten (Firma/Typ, E-Mail, Telefon getrennt + Letzter Kontakt) */}
            <table className="w-full min-w-[880px] border-collapse text-left">
              <thead>
                <tr>
                  <th className="table-header">Kunde</th>
                  <th className="table-header">Firma / Typ</th>
                  <th className="table-header">E-Mail</th>
                  <th className="table-header">Telefon</th>
                  <th className="table-header text-right">Events</th>
                  <th className="table-header text-right">Umsatz</th>
                  <th className="table-header text-right">Letzter Kontakt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/30 dark:divide-outline/30">
                {filtered.map((c) => {
                  const evs = events.filter((e) => e.customerId === c.id);
                  const revenue = evs
                    .filter((e) => ["paid", "completed"].includes(e.status))
                    .reduce((s, e) => s + (e.amountTotalCents ?? 0), 0);
                  const openCents = evs
                    .filter((e) => OPEN_EVENT_STATUS.has(e.status))
                    .reduce((s, e) => s + (e.amountTotalCents ?? 0), 0);
                  // H3: verlorener Kunde ausgegraut, wenn alle bisherigen Vorgaenge im Sand verliefen.
                  const isLost = evs.length > 0 && evs.every((e) => LOST_EVENT_STATUS.has(e.status));
                  const lastContact = [c.createdAt, ...evs.map((e) => e.createdAt)].sort().at(-1) ?? c.createdAt;
                  const type = customerType(c);
                  return (
                    <tr
                      key={c.id}
                      className={`group cursor-pointer transition-colors hover:bg-surface-container-high/20 dark:hover:bg-surface-variant/20 ${isLost ? "opacity-55" : ""}`}
                      onClick={() => navigate(`/kunden/${c.id}`)}
                    >
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-label-md text-label-md font-bold ${
                              isLost ? "bg-surface-variant text-secondary" : "bg-primary/14 text-primary"
                            }`}
                          >
                            {initials(c.name)}
                          </div>
                          <div className="min-w-0 font-body-md text-body-md font-semibold text-on-background transition-colors group-hover:text-primary dark:text-inverse-on-surface">
                            {c.name}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                        {c.company ?? <span className="text-secondary-fixed-dim">{type === "privat" ? "Privat" : "—"}</span>}
                      </td>
                      <td className="table-cell truncate font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">{c.email}</td>
                      <td className="table-cell font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">{c.phone ?? "–"}</td>
                      <td className="table-cell text-right font-body-md text-body-md text-on-surface-variant dark:text-secondary-fixed-dim">
                        {evs.length}
                      </td>
                      <td className="table-cell text-right font-body-md text-body-md font-semibold">
                        {revenue > 0 ? (
                          <span className="text-on-background dark:text-inverse-on-surface">{money(revenue)}</span>
                        ) : openCents > 0 ? (
                          <span className="text-[#96650D] dark:text-[#e0b366]">{money(openCents)} offen</span>
                        ) : (
                          <span className="font-medium text-secondary-fixed-dim">—</span>
                        )}
                      </td>
                      <td className="table-cell text-right font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                        {formatDate(lastContact)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {topCustomer && topCustomer.revenue > 0 && (
              <div className="flex items-center justify-between border-t border-outline-variant/30 bg-surface-container-low px-4 py-3 dark:border-outline/30 dark:bg-surface-variant/10">
                <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {filtered.length} von {customers.length} Kunden
                </span>
                <span className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  Top-Kunde: {topCustomer.name} · {money(topCustomer.revenue)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Anlegen */}
      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title="Kunde anlegen">
        <form className="space-y-4" onSubmit={(e) => void handleCreate(e)}>
          {formError && <ErrorNote message={formError} />}
          <label className="block">
            <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Name *
            </span>
            <input name="name" required className="input" />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              E-Mail *
            </span>
            <input name="email" type="email" required className="input" />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Firma
            </span>
            <input name="company" className="input" />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Telefon
            </span>
            <input name="phone" className="input" />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setCreateOpen(false)}>
              Abbrechen
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? "Speichert …" : "Kunde anlegen"}
            </button>
          </div>
        </form>
      </Drawer>
    </>
  );
}
